/**
 * THE SECOND ALLOW-LIST.
 *
 * A product description that came from a merchant's WordPress arrives here as
 * an allow-listed subset — the server tokenised the original and RE-SERIALISED
 * it (`catalog-migration/html-subset.ts`), so it can only contain the sixteen
 * tags below and carries no attribute of any kind. This file does not trust
 * that, because trusting it would mean one mistake anywhere upstream — a
 * portal edit, a future importer, a migration that back-fills a column — puts
 * merchant markup on our storefront.
 *
 * So the subset is PARSED into plain objects and the renderer builds React
 * elements from them. Three consequences, and they are the whole argument:
 *
 *   - `dangerouslySetInnerHTML` is never used, here or in the renderer. There
 *     is no code path that turns a string into markup.
 *   - No attribute is ever read. A node is `{ type, children }` or
 *     `{ type: 'text', text }`; there is nowhere for an `onerror`, an `href`
 *     or a `style` to live even if one arrived.
 *   - A tag outside the list produces no node. Its TEXT survives when it is a
 *     wrapper (a `<div>` the merchant's editor left behind), and goes with it
 *     when the element's content is not prose (`<script>`, `<style>`).
 *
 * `DOMParser` is used for the parse itself and is inert by construction: the
 * document it returns has no browsing context, so scripts do not run and no
 * resource — image, stylesheet, frame — is fetched.
 */

/** Exactly the tags the server's subset can contain. */
export const RICH_TEXT_TAGS = [
  'p', 'br', 'hr', 'strong', 'em', 'ul', 'ol', 'li', 'h3', 'h4',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

const ALLOWED = new Set(RICH_TEXT_TAGS);

/** Elements whose content is not prose: they go, and their text goes with them. */
const CONTENT_NOT_PROSE = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg',
  'math', 'form', 'select', 'textarea', 'option', 'button', 'canvas', 'audio',
  'video', 'picture', 'map', 'head', 'title',
]);

const VOID_NODES = new Set(['br', 'hr']);

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

function collect(parent) {
  const out = [];
  const kids = parent && parent.childNodes ? Array.from(parent.childNodes) : [];
  for (const node of kids) {
    if (node.nodeType === TEXT_NODE) {
      const text = String(node.nodeValue || '');
      // Whitespace between two block tags is source indentation, not a word gap.
      if (text.trim() === '') continue;
      out.push({ type: 'text', text: text.replace(/\s+/g, ' ') });
      continue;
    }
    if (node.nodeType !== ELEMENT_NODE) continue; // comments, CDATA, the rest
    const tag = String(node.tagName || '').toLowerCase();
    if (CONTENT_NOT_PROSE.has(tag)) continue;
    if (!ALLOWED.has(tag)) { out.push(...collect(node)); continue; } // unwrap
    if (VOID_NODES.has(tag)) { out.push({ type: tag }); continue; }
    out.push({ type: tag, children: collect(node) });
  }
  return out;
}

/**
 * The subset → a tree of `{ type, children }` / `{ type: 'text', text }`.
 * Never throws, and answers `[]` for anything that is not a non-empty string.
 */
export function parseRichText(html) {
  if (typeof html !== 'string' || html.trim() === '') return [];
  if (typeof DOMParser === 'undefined') return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (e) {
    return [];
  }
  return collect(doc && doc.body);
}

const LINE_ENDS = new Set(['p', 'h3', 'h4', 'li', 'tr', 'br', 'hr']);
const CELL_SEPARATOR = ' — ';

/** The same tree as the plain string — for a length test, a title, a summary. */
export function richTextToPlain(nodes) {
  let out = '';
  const walk = (list) => {
    for (const node of list || []) {
      if (!node) continue;
      if (node.type === 'text') { out += node.text; continue; }
      if (node.type === 'br' || node.type === 'hr') { out += '\n'; continue; }
      walk(node.children);
      if (node.type === 'td' || node.type === 'th') out += CELL_SEPARATOR;
      else if (LINE_ENDS.has(node.type)) out += '\n';
    }
  };
  walk(nodes);
  return out
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim().replace(/(?:\s*—\s*)+$/, '').trim())
    .filter((line, i, all) => line !== '' || (i > 0 && all[i - 1] !== ''))
    .join('\n')
    .trim();
}

/**
 * How much text a description actually holds — what the show-more decision is
 * taken on. Measured on the CONTENT rather than on a rendered height, so it is
 * the same answer in a test, on a phone and on a desktop.
 */
export function richTextLength(nodes) {
  return richTextToPlain(nodes).length;
}
